import type { EventType } from '@cocoonspace/sdk-js/types/eventType'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { SpaceHours } from '@cocoonspace/sdk-js/types/spaceHours'
import { listSpaceAvailabilities } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import {
	createBooking,
	updateBooking,
} from '@cocoonspace/sdk-js/domains/bookings/bookings'
import { createBookingWithEvent } from '@cocoonspace/sdk-js/domains/events/events'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useMutation } from '@tanstack/react-query'

import { getWallet } from '~/domains/account/components/wallet/use-wallet.hook'
import { useUserContext } from '~/providers/user-provider'
import { shouldAddWallet } from '../utils/should-add-wallet'

export const useCreateBookingOptions = () => {
	const { userQuery, isBusiness } = useUserContext()

	return useMutation({
		mutationFn: async ({
			bookingOptions,
			space,
			bookingRequestID,
		}: {
			bookingOptions: {
				day: string
				start?: string
				end?: string
			}[]
			space: Pick<Space, 'id' | 'hours'>
			bookingRequestID?: string
		}) => {
			// Preparing payload
			const eventsPayload = bookingOptions.map(({ day, start, end }) => {
				const date = dateTimeUtils(day)

				const basePayload = {
					day: date.customFormat('dateTech'),
					space: space.id,
					type: 'meeting' as EventType,
				}

				if (start && end) {
					return { ...basePayload, end, start }
				}

				const dayLabel = date
					.locale('en')
					.format('ddd')
					.toLowerCase() as keyof SpaceHours

				const { from, to } = space.hours[dayLabel]?.open?.[0] ?? {
					from: '',
					to: '',
				}

				return {
					...basePayload,
					end: `${to}`,
					start: `${from}`,
				}
			})

			// Check availabillity (just in case)
			const unavailable = await listSpaceAvailabilities(eventsPayload).then(
				(res) => {
					return res.data.filter(({ available }) => !available)
				},
			)

			if (unavailable.length) {
				return Promise.reject({
					data: unavailable,
					message: 'unavailable',
				})
			}

			// Create empty booking
			const booking = await createBooking({
				booking_request_id: bookingRequestID,
			}).then(({ data }) => data)

			// Create events for this booking
			const eventsQueries = eventsPayload.map(({ day, start, end, space }) => {
				const payload = {
					booking: booking?.id,
					day,
					end,
					space,
					start,
				}

				return createBookingWithEvent(payload).then((res) => res.data)
			})

			const events = await Promise.all(eventsQueries)

			const wallet = await getWallet()

			const defaultRefundModeIsWallet =
				userQuery.data?.default_refund_mode === 'wallet'

			if (
				shouldAddWallet({
					isBusiness,
					events,
					walletAmount: wallet?.amount,
					defaultRefundModeIsWallet,
				})
			) {
				await updateBooking(booking.id, { has_wallet: true })
				// if (hasWalletRefundOnBooking) {
				// } else {
				// 	const updateEventsQuries = events.map((event) => {
				// 		return updateEventExtra(event.id, 'WALLET', { quantity: 1 })
				// 	})
				//
				// 	await Promise.all(updateEventsQuries)
				// }
			}

			// Return booking
			return booking
		},
	})
}
